"use client";

import { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Phone,
  Email,
  Chat,
  Send,
  PhoneRounded,
  Person,
} from "@mui/icons-material";
import FileUploadArea from "./common/FileUploadArea";
import { useFileUploadMP } from "../../../../hooks/useUploadFileMP";
import {
  SUPPORT_PRIORITY_LABELS,
  SUPPORT_CATEGORY_LABELS,
  TICKET_STATUS,
  TICKET_STATUS_COLORS,
} from "./constants/equipment-data";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { UrlApi } from "../../../../../../services/apirest";
import { useDocuments } from "../../../../hooks/useDocuments";
import { DownloadFiles } from "../../../../../../services/DownloadFiles";
import DocumentCard from "./common/DocumentCard";
import { useToast } from "../../../../../../Hooks/UseToast";

export default function TechnicalSupportTab() {
  const { equipo } = useLocation().state;
  const [supportForm, setSupportForm] = useState({
    telefono_soporte: "",
    email_soporte: "",
    persona_soporte: "",
  });
  const [dataSupport, setDataSupport] = useState({});
  const [errors, setErrors] = useState({});
  const fileId = equipo.uuid_documentacion;

  const { uploadFile, isUploading, uploadProgress } = useFileUploadMP();
  const { removeDocument, getDocuments, documents, ViewFiles } = useDocuments(
    []
  );
  const { showToast, Toast } = useToast();

  const refetchSoporteDocs = () => getDocuments(fileId, "soporte");

  useEffect(() => {
    if (fileId) refetchSoporteDocs();
  }, [fileId]);

  // 🔹 Subida de archivos
  const handleFileSelect = async (files) => {
    if (!fileId) {
      console.log("No hay ID de archivo");
      return;
    }

    try {
      const data = await uploadFile(files, "soporte", fileId);
      console.log("Archivo subido:", data);

      await refetchSoporteDocs();
      showToast("Archivo cargado con éxito", "success");
    } catch (err) {
      showToast("Error al subir archivo", "error");
    }
  };

  const getSupport = useCallback(async () => {
    await axios
      .get(UrlApi + "/mp_equipos/byid/" + equipo.id)
      .then((res) => {
        console.log(res.data);
        setDataSupport(res?.data[0]);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [equipo.id]);

  useEffect(() => {
    getDocuments(fileId, "soporte");
    getSupport();
  }, [fileId, getDocuments, getSupport]);

  const handleDocumentView = (document) => {
    ViewFiles(document.id_archivo + "-" + document.nombre, document.extension);
    // Implementar lógica de vista previa
  };

  const handleDocumentDownload = (document) => {
    DownloadFiles(document.id_archivo + "-" + document.nombre);
    // Implementar lógica de descarga
  };
  const handleDocumentDelete = async (doc) => {
    await removeDocument(doc.id, doc.nombre);
    await refetchSoporteDocs();
    showToast("Archivo eliminado", "info");
  };
  const validateForm = () => {
    const newErrors = {};

    // Expresiones Regulares para validación de formato
    // Puedes ajustar estas expresiones según los formatos exactos que necesites
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Esta regex de teléfono acepta solo dígitos, con un mínimo de 7 y un máximo de 15,
    // pero puedes modificarla para aceptar espacios, guiones, o códigos de país.
    const phoneRegex = /^\d{7,15}$/;

    // --- Validación de Teléfono ---
    if (!supportForm.telefono_soporte) {
      newErrors.telefono_soporte = "El campo Teléfono es obligatorio";
    } else if (!phoneRegex.test(supportForm.telefono_soporte)) {
      newErrors.telefono_soporte =
        "El formato del Teléfono no es válido (solo números, 7-15 dígitos)";
    }

    // --- Validación de Email ---
    if (!supportForm.email_soporte) {
      newErrors.email_soporte = "El campo Email es obligatorio";
    } else if (!emailRegex.test(supportForm.email_soporte)) {
      newErrors.email_soporte = "El formato del Email no es válido";
    }

    // --- Validación de Persona de Soporte (Mantenida) ---
    if (!supportForm.persona_soporte) {
      newErrors.persona_soporte = "El campo Persona de Soporte es obligatorio";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSupportForm({
      ...supportForm,
      [name]: value,
    });

    // Limpiar error cuando el usuario corrige el campo
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };
  const contactInfo = [
    {
      icon: Phone,
      title: "Telefono",
      subtitle: dataSupport?.telefono_soporte,
      color: "success.main",
    },
    {
      icon: Email,
      title: "Email",
      subtitle: dataSupport?.email_soporte,
      color: "info.main",
    },
    {
      icon: Person,
      title: "Persona de Soporte",
      subtitle: dataSupport?.persona_soporte,
      color: "secondary.light",
    },
  ];

  const handleSubmitSupport = async () => {
    // Implementar lógica de envío
    try {
      if (validateForm()) {
        await axios.put(UrlApi + "/mp_equipos/" + equipo.id, supportForm);
        await getSupport();
      }
    } catch (error) {
      console.error("Error al enviar el formulario de soporte:", error);
    }
  };

  return (
    <Grid container spacing={3}>
      {Toast}
      <Grid item xs={12} lg={6}>
        <Card>
          <CardHeader title="Equipo" subheader="Información del Equipo" />
          <CardContent>
            <Divider sx={{ my: 1 }} />

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Equipo:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">{equipo?.equipo}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Serie:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">{equipo?.serie_equipo}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Marca:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  {equipo?.marca}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Modelo:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  {equipo?.modelo}
                </Typography>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid item xs={12}>
                <FileUploadArea
                  title="Archivos Adjuntos"
                  description="Adjunta fotos, videos o logs del problema"
                  acceptedTypes="*"
                  onFileSelect={handleFileSelect}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Card>
          <CardHeader
            title="Contacto de Soporte Técnico"
            subheader="Información de contacto para soporte técnico y emergencias"
          />
          <CardContent>
            {dataSupport?.telefono_soporte &&
            dataSupport?.email_soporte &&
            dataSupport?.persona_soporte ? (
              <List>
                {contactInfo.map((contact, index) => {
                  const IconComponent = contact.icon;
                  return (
                    <ListItem key={index}>
                      <Avatar sx={{ bgcolor: contact.color, mr: 2 }}>
                        <IconComponent />
                      </Avatar>
                      <ListItemText
                        primary={contact.title}
                        secondary={contact.subtitle}
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Grid container direction="column" columnSpacing={2} spacing={2}>
                <Grid item columns={2} xs={2}>
                  <TextField
                    fullWidth
                    focused
                    id="input-with-icon-textfield"
                    type="number"
                    label="Telefono"
                    error={!!errors.telefono_soporte}
                    helperText={errors.telefono_soporte}
                    name="telefono_soporte"
                    value={supportForm.telefono_soporte}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <Avatar sx={{ bgcolor: "success.main", mr: 2, my: 2 }}>
                          <PhoneRounded color="white" />
                        </Avatar>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item columns={2} xs={2}>
                  <TextField
                    fullWidth
                    focused
                    id="input-with-icon-textfield"
                    label="Email"
                    value={supportForm.email_soporte}
                    error={!!errors.email_soporte}
                    helperText={errors.email_soporte}
                    name="email_soporte"
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <Avatar sx={{ bgcolor: "info.main", mr: 2, my: 2 }}>
                          <Email color="white" />
                        </Avatar>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item columns={2} xs={2}>
                  <TextField
                    fullWidth
                    focused
                    id="input-with-icon-textfield"
                    label="Persona de Soporte"
                    value={supportForm.persona_soporte}
                    name="persona_soporte"
                    error={!!errors.persona_soporte}
                    helperText={errors.persona_soporte}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <Avatar
                          sx={{ bgcolor: "secondary.light", mr: 2, my: 2 }}
                        >
                          <Person color="white" />
                        </Avatar>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Send />}
                    size="large"
                    onClick={handleSubmitSupport}
                  >
                    Enviar Solicitud de Soporte
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Documentos Cargados</Typography>
          <Chip label={`${documents?.length} documentos`} variant="outlined" />
        </Box>

        {documents?.map((doc) => (
          <DocumentCard
            key={doc.id}
            document={doc}
            onView={handleDocumentView}
            onDownload={handleDocumentDownload}
            onDelete={handleDocumentDelete}
            showCategory={false}
          />
        ))}
      </Grid>
    </Grid>
  );
}
